import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { RootState } from '../../../redux/store';
import { useAppSelector } from '../../../redux/hooks';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { adminRoutes } from '../../../routers/AdminRouters';

import {
    useGetSessionSummaryBySessionIdQuery,
    useGetSessionFormatQuery
} from '../../../services';
import SessionSummary from './exercisebuildercomponents/SessionSummary';

type Props = {}

const SessionSummaryTable: React.FC<Props> = () => {
    const { theme } = useTheme();
    const themeBox = theme === "dark";
    const pageBg = themeBox ? "bg-[#0B0B0B]" : "bg-gray-50";

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const categoryId = searchParams.get("category");

    type NavbarSliceState = { isOpen: boolean };
    const isSidebarOpen = useAppSelector(
        (state: RootState) => (state.navbar as NavbarSliceState).isOpen
    );
    const sidebarWidth = isSidebarOpen ? 256 : 64;

    // Extract sessionid from URL
    const { sessionid } = useParams<{ sessionid?: string }>();

    // Fetch required data - ADDED refetchOnMountOrArgChange to fix the "refresh" bug
    const {
        data: summaryQueryResult,
        isLoading: isSummaryLoading,
        isFetching: isSummaryFetching
    } = useGetSessionSummaryBySessionIdQuery(
        {
            sessionid: sessionid ?? "",
            limit: 100, // 🔥 important
            page: 1,
        },
        {
            skip: !sessionid,
            refetchOnMountOrArgChange: true,
        }
    );


    const {
        data: sessionFormat,
        isLoading: isFormatLoading,
        isFetching: isFormatFetching
    } = useGetSessionFormatQuery(
        sessionid ?? "",
        { skip: !sessionid, refetchOnMountOrArgChange: true }
    );


    console.log("summaryQueryResult", summaryQueryResult);
    console.log("sessionFormat", sessionFormat)
    // Combine loading states so UI waits for both API calls
    const isPageLoading = isSummaryLoading || isFormatLoading || isSummaryFetching || isFormatFetching;

    const formatList = sessionFormat?.results || [];
    const allSessionSummaryData = summaryQueryResult?.results || [];

    // State for Sidebar Navigation
    const [isSessionSummarySelected, setIsSessionSummarySelected] = useState(true);
    const [selectedSubFormatIdx, setSelectedSubFormatIdx] = useState<number | null>(null);
    const [selectedFormatIdx, setSelectedFormatIdx] = useState(0);

    const selectedFormat = formatList[selectedFormatIdx];
    const selectedFormatId = selectedFormat?._id;
    const selectedFormatName = selectedFormat?.name || "Format";
    // Fallback to blockTiming if duration isn't present
    const selectedFormatDuration = selectedFormat?.blockTiming || selectedFormat?.duration || 0;

    // Derived data based on selection
    const displayedSummaryData = selectedSubFormatIdx !== null
        ? allSessionSummaryData.filter((item: any) => item.format === selectedFormatId)
        : allSessionSummaryData;

    // Handlers
    const handleSessionSummaryClick = () => {
        setIsSessionSummarySelected(true);
        setSelectedSubFormatIdx(null);
    };

    const handleSubFormatClick = (idx: number) => {
        setSelectedSubFormatIdx(idx);
        setSelectedFormatIdx(idx);
    };

    const handleFormatClick = (idx: number) => {
        setIsSessionSummarySelected(false);
        setSelectedSubFormatIdx(idx);
        setSelectedFormatIdx(idx);
    };

    // Helper to format block time in the sidebar
    const formatTimeSidebar = (seconds: number) => {
        if (!seconds) return "";
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    return (
        <div
            className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
            style={{
                marginLeft: sidebarWidth,
                width: `calc(100% - ${sidebarWidth}px)`,
            }}
        >
            <style>{`
                .scroll-area {
                  scrollbar-width: thin;
                  scrollbar-color: var(--scroll-thumb) var(--scroll-track);
                }
                .scroll-area::-webkit-scrollbar {
                  width: 10px;
                  height: 10px;
                }
                .scroll-area::-webkit-scrollbar-track {
                  background: var(--scroll-track);
                  border-radius: 8px;
                }
                .scroll-area::-webkit-scrollbar-thumb {
                  background-color: var(--scroll-thumb);
                  border-radius: 8px;
                  border: 2px solid var(--scroll-track);
                }
            `}</style>

            <div className="mb-6">
                <h1 className={`text-xl font-semibold ${themeBox ? "text-gray-300" : "text-gray-800"}`}>
                    Session Summary Table
                </h1>
                <div className="flex items-center gap-2 mt-2 text-sm">
                    <Link
                        to={`${adminRoutes.sessionList}`}
                        className={themeBox ? "text-gray-400" : "text-gray-600"}
                    >
                        All Sessions
                    </Link>
                    <span className={themeBox ? "text-gray-400" : "text-gray-600"}>
                        /
                    </span>
                    <span className={themeBox ? "text-[#B6CB24]" : "text-lime-600"}>
                        Session Summary Table
                    </span>
                </div>
            </div>

            {/* Main Content Container with Sidebar */}
            <div className={`rounded-lg border ${themeBox ? "bg-[#121212] border-[#232323]" : "bg-white border-gray-200"} relative overflow-hidden`}>
                <div className="flex">

                    {/* LEFT SIDEBAR: Session Summary Timeline */}
                    <div className={`flex flex-col w-64 border-r ${themeBox ? "border-[#232323]" : "border-gray-200"} py-4`}>
                        <h4 className={`px-4 pb-6 text-sm font-semibold ${themeBox ? "text-gray-100" : "text-gray-800"}`}>
                            Session Summary
                        </h4>
                        <div className="px-5 relative">
                            <div className="flex flex-col">
                                {formatList.map((fmt: any, idx: number) => {
                                    const isActive = selectedFormatIdx === idx && !isSessionSummarySelected;
                                    const blockTime = fmt.blockTiming || fmt.duration || 0;
                                    const restTime = fmt.restPeriod || 0;

                                    return (
                                        <div key={fmt._id} className="relative z-10 flex items-stretch gap-4">
                                            {/* Timeline Dot & Line */}
                                            <div className="flex flex-col items-center flex-shrink-0">
                                                <div className={`mt-2.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${isActive
                                                    ? "border-[#B6CB24] bg-[#B6CB24] shadow-[0_0_8px_rgba(182,203,36,0.6)]"
                                                    : themeBox
                                                        ? "border-[#444] bg-[#121212]"
                                                        : "border-gray-300 bg-white"
                                                    }`}
                                                >
                                                    {isActive && (
                                                        <div className="w-1.5 h-1.5 rounded-full bg-black/20" />
                                                    )}
                                                </div>
                                                {/* Line to next node */}
                                                <div className="flex-1 w-0 border-l border-dashed border-[#444] min-h-[40px]" />
                                            </div>

                                            {/* Format Content */}
                                            <div className="flex-1 min-w-0 pb-6">
                                                <button
                                                    onClick={() => handleFormatClick(idx)}
                                                    className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${isActive
                                                        ? themeBox
                                                            ? "bg-[#B6CB24]/10 border-[#B6CB24] text-[#F3F3F6]"
                                                            : "bg-lime-50 border-lime-500 text-lime-900"
                                                        : themeBox
                                                            ? "bg-transparent border-[#232323] text-gray-400 hover:border-[#444]"
                                                            : "bg-transparent border-gray-200 text-gray-600 hover:border-gray-300"
                                                        }`}
                                                >
                                                    <span className="text-sm font-semibold truncate block">
                                                        {fmt.name}
                                                    </span>


                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="relative z-10 flex flex-col">
                                    <div className="flex items-start gap-4">
                                        {/* Timeline Dot for Session Summary */}
                                        <div className="flex flex-col items-center flex-shrink-0">
                                            <div className={`mt-2.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${isSessionSummarySelected
                                                ? "border-[#B6CB24] bg-[#B6CB24] shadow-[0_0_8px_rgba(182,203,36,0.6)]"
                                                : themeBox
                                                    ? "border-[#444] bg-[#121212]"
                                                    : "border-gray-300 bg-white"
                                                }`}
                                            >
                                                {isSessionSummarySelected && (
                                                    <div className="w-1.5 h-1.5 rounded-full bg-black/20" />
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <button
                                                onClick={handleSessionSummaryClick}
                                                className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${isSessionSummarySelected
                                                    ? themeBox
                                                        ? "bg-[#B6CB24]/10 border-[#B6CB24] text-[#F3F3F6]"
                                                        : "bg-lime-50 border-lime-500 text-lime-900"
                                                    : themeBox
                                                        ? "bg-transparent border-[#232323] text-gray-400 hover:border-[#444]"
                                                        : "bg-transparent border-gray-200 text-gray-600 hover:border-gray-300"
                                                    }`}
                                            >
                                                <span className="text-sm font-semibold truncate block">
                                                    Overall Summary
                                                </span>
                                            </button>

                                            {/* Sub-formats stacked under Summary */}
                                            {isSessionSummarySelected && (
                                                <div className="mt-4 flex flex-col items-start px-4 space-y-3">
                                                    {formatList.map((fmt: any, idx: number) => {
                                                        const blockTime = fmt.blockTiming || fmt.duration || 0;
                                                        const restTime = fmt.restPeriod || 0;

                                                        return (
                                                            <button
                                                                key={`summary-${fmt._id}`}
                                                                onClick={() => handleSubFormatClick(idx)}
                                                                className={`text-sm transition-colors text-left flex flex-col ${selectedSubFormatIdx === idx
                                                                    ? "text-[#B6CB24] font-bold"
                                                                    : "text-gray-400 hover:text-gray-300"
                                                                    }`}
                                                            >
                                                                <span>{fmt.name}</span>

                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT SIDE: Table Content */}
                    <div className="flex-1">
                        {isPageLoading ? (
                            <div className="p-12 text-center">
                                <div className={`text-sm ${themeBox ? "text-gray-400" : "text-gray-600"}`}>
                                    Loading session summary...
                                </div>
                            </div>
                        ) : (
                            <SessionSummary
                                themeBox={themeBox}
                                selectedFormatId={selectedFormatId || ""}
                                sessionSummaryData={displayedSummaryData}
                                formatName={selectedFormatName}
                                duration={selectedFormatDuration}
                                formatList={formatList}
                                isSessionSummary={selectedSubFormatIdx === null}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SessionSummaryTable;